import React, { useState, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  createSingleSavedQuestion,
  getAccountState,
} from "../../../../redux/slices/accountSlice";
import {
  GuestInfo,
  NotificationType,
  SavedFormQuestion,
  SpreadsheetInfo,
} from "@markit/common.types";
import { Colors } from "../../../../utils/colors";
import { Icon } from "@iconify/react";
import { useOnMount } from "../../../../hooks/useOnMount";
import FormSavedQuestions, {
  FormSavedQuestionsType,
} from "../../../Form/FormSavedQuestions";
import "../../../../css/Table.css";
import {
  deepCopy,
  detectedCensored,
  handlePluralString,
  uniqueVals,
} from "@markit/common.utils";
import { DropdownMenu, DropdownMenuItem } from "../../../DropdownMenu";
import { FullNameColumnLabel } from "../../../../screens/Import/ImportContacts";
import RectangleButton from "../../../Buttons/RectangleButton";
import CustomCheckbox from "../../../CustomCheckbox";
import CombineColumnsModal from "./CombineColumnsModal";
import {
  getCombineColumnsSeparator,
  SeparatorType,
} from "../../../../utils/spreadsheetUtils";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../../../Containers/PopupModalContainer";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import { showNotificationBanner } from "../../../../utils/notificationUtils";

type AssignImportedContactsProps = {
  spreadsheet: SpreadsheetInfo;
  setSpreadsheet: (spreadsheet: SpreadsheetInfo) => void;
  includeExistingContacts: boolean;
  assignedColumns: string[];
  setAssignedColumns: (assignedColumns: string[]) => void;
};

const AssignImportedContacts = (props: AssignImportedContactsProps) => {
  const {
    spreadsheet,
    setSpreadsheet,
    includeExistingContacts,
    assignedColumns,
    setAssignedColumns,
  } = props;
  const dispatch = useDispatch();
  const { accountData, savedQuestions } = useSelector(getAccountState).account;
  const [questionsVisible, setQuestionsVisible] = useState(false);
  const [formQuestionItem, setFormQuestionItem] = useState<SavedFormQuestion>();
  const [columnIndex, setColumnIndex] = useState(-1);
  const [newQuestionOptions, setNewQuestionOptions] = useState<string[]>([]);
  const [isCombineColumns, setIsCombineColumns] = useState(false);
  const [confirmCombineColumns, setConfirmCombineColumns] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<number[]>([]);
  const [fullNameAlertConfirmModal, setFullNameAlertConfirmModal] = useState<{
    type: "empty" | "censored" | "";
    index: number;
  }>({ type: "", index: -1 });

  const styles = {
    headerText: { fontSize: 20, fontWeight: 500 },
    subtext: { fontSize: 14, color: Colors.GRAY1 },
    leftCellContainer: {
      paddingInline: 24,
      paddingBlock: 20,
      whiteSpace: "nowrap",
    },
    cellContainer: {
      paddingInline: 40,
      paddingBlock: 20,
      whiteSpace: "nowrap",
    },
  };

  const spreadsheetRows = useMemo(
    () =>
      spreadsheet
        ? includeExistingContacts
          ? spreadsheet.validRows.concat(spreadsheet.existingContactRows)
          : spreadsheet.validRows
        : [],
    [includeExistingContacts, spreadsheet]
  );

  const foundFullNameColumn = useMemo(
    () => assignedColumns.some((column) => column === FullNameColumnLabel),
    [assignedColumns]
  );

  const censoredTermsFound = useCallback(
    (index: number) =>
      spreadsheetRows
        .filter(
          (row) => row.dataColumns && detectedCensored(row.dataColumns[index])
        )
        .map((item) => (item.dataColumns ? item.dataColumns[index] : "")),
    [spreadsheetRows]
  );

  // Helper function to check if a row has an empty, censored (or both) cell at the specified column index
  const hasEmptyOrCensoredCell = useCallback(
    (
      row: GuestInfo,
      columnIndex: number,
      type: "empty" | "censored" | ""
    ): boolean => {
      const cellValue = row.dataColumns?.[columnIndex];
      if (cellValue === undefined || cellValue === null) return true;
      if (type === "empty") return /^\s*$/.test(cellValue);
      if (type === "censored") return detectedCensored(cellValue);
      return /^\s*$/.test(cellValue) || detectedCensored(cellValue);
    },
    []
  );

  const modifySpreadsheetItemNames = useCallback(
    (isAssigning: boolean, index: number) => {
      if (isAssigning) {
        // Check if this column has any empty items (full name must all be populated)
        const hasEmptyFullName = spreadsheetRows.some(
          (row) => row.dataColumns && /^\s*$/.test(row.dataColumns[index])
        );
        if (hasEmptyFullName) {
          setFullNameAlertConfirmModal({ type: "empty", index: index });
          return undefined;
        }
        // Check if column items contains any censored terms
        if (censoredTermsFound(index).length > 0) {
          setFullNameAlertConfirmModal({ type: "censored", index: index });
          return undefined;
        }
      }

      const newValidRows: GuestInfo[] = spreadsheet.validRows.map((row) => {
        return {
          ...row,
          fullName:
            isAssigning && row.dataColumns ? row.dataColumns[index] : "",
        };
      });
      const newAlreadyExistingRows: GuestInfo[] =
        spreadsheet.existingContactRows.map((row) => {
          return {
            ...row,
            fullName:
              isAssigning && row.dataColumns ? row.dataColumns[index] : "",
          };
        });
      const newSpreadsheet: SpreadsheetInfo = {
        ...spreadsheet,
        validRows: newValidRows,
        existingContactRows: newAlreadyExistingRows,
      };
      return newSpreadsheet;
    },
    [censoredTermsFound, spreadsheet, spreadsheetRows]
  );

  const assignFullName = useCallback(
    (index: number) => {
      const newSpreadsheet = modifySpreadsheetItemNames(true, index);
      if (newSpreadsheet) {
        setSpreadsheet(newSpreadsheet);
        const assignedColumnsCopy = deepCopy(assignedColumns);
        assignedColumnsCopy.splice(index, 1, FullNameColumnLabel);
        setAssignedColumns(assignedColumnsCopy);
      }
      setColumnIndex(-1);
    },
    [
      assignedColumns,
      modifySpreadsheetItemNames,
      setAssignedColumns,
      setSpreadsheet,
    ]
  );

  useOnMount(() => {
    if (spreadsheetRows[0].dataColumns && assignedColumns.length === 0) {
      // Add full name column if there is only one dataColumn, and there are no empty or censored cells in the column
      if (
        spreadsheetRows[0].dataColumns.length === 1 &&
        spreadsheetRows.every((row) => !hasEmptyOrCensoredCell(row, 0, ""))
      ) {
        assignFullName(0);
      } else {
        setAssignedColumns(
          new Array(spreadsheetRows[0].dataColumns.length).fill("")
        );
      }
    }
  });

  const unassignFullName = useCallback(
    (index: number) => {
      const newSpreadsheet = modifySpreadsheetItemNames(false, index);
      if (newSpreadsheet) {
        setSpreadsheet(newSpreadsheet);
        const assignedColumnsCopy = deepCopy(assignedColumns);
        assignedColumnsCopy.splice(index, 1, "");
        setAssignedColumns(assignedColumnsCopy);
      }
      setColumnIndex(-1);
    },
    [
      assignedColumns,
      modifySpreadsheetItemNames,
      setAssignedColumns,
      setSpreadsheet,
    ]
  );

  const assignQuestionOnPress = useCallback(
    (selectedQuestion: SavedFormQuestion) => {
      // Another column has the selected question assigned already
      if (
        selectedQuestion &&
        assignedColumns.some((column) => column === selectedQuestion.id) &&
        formQuestionItem?.id !== selectedQuestion.id
      ) {
        return false;
      }
      // Assigning/Unassigning column to question
      const assignedColumnsCopy = deepCopy(assignedColumns);
      assignedColumnsCopy.splice(
        columnIndex,
        1,
        selectedQuestion ? selectedQuestion.id : ""
      );
      setAssignedColumns(assignedColumnsCopy);
      if (selectedQuestion) {
        dispatch(createSingleSavedQuestion(accountData.uid, selectedQuestion));
      }
      setColumnIndex(-1);
      setNewQuestionOptions([]);
      setFormQuestionItem(undefined);
      return true;
    },
    [
      accountData.uid,
      assignedColumns,
      columnIndex,
      dispatch,
      formQuestionItem?.id,
      setAssignedColumns,
    ]
  );

  const combineColumnsOnPress = useCallback(
    (separator: SeparatorType) => {
      if (selectedColumns.length < 2) return;

      const separatorString = getCombineColumnsSeparator(separator);

      // The first column will be replaced with the combined value
      const firstColumnIndex = selectedColumns[0];
      const otherColumnIndices = selectedColumns.slice(1);

      const columnsToRemoveSet = new Set(otherColumnIndices);

      const combineRowColumns = (row: GuestInfo): GuestInfo => {
        if (!row.dataColumns) return row;

        // Combine the selected columns in the order specified by selectedColumns
        // Filter out empty strings to avoid trailing/leading separators
        const combinedValue = selectedColumns
          .map((colIndex) => row.dataColumns?.[colIndex] ?? "")
          .filter((value) => value && value.trim() !== "")
          .join(separatorString);

        // Build new array in a single pass: O(n) per row instead of O(n*m) with splice
        // This is much more efficient for large datasets (3000+ rows)
        const newDataColumns: string[] = [];
        for (let i = 0; i < row.dataColumns.length; i++) {
          if (i === firstColumnIndex) {
            newDataColumns.push(combinedValue);
          } else if (!columnsToRemoveSet.has(i)) {
            newDataColumns.push(row.dataColumns[i]);
          }
        }

        return { ...row, dataColumns: newDataColumns };
      };

      const newValidRows = spreadsheet.validRows.map(combineRowColumns);
      const newExistingContactRows =
        spreadsheet.existingContactRows.map(combineRowColumns);

      // Build new assignedColumns array efficiently without splice
      const newAssignedColumns = assignedColumns.filter(
        (_, index) => !columnsToRemoveSet.has(index)
      );

      setSpreadsheet({
        ...spreadsheet,
        validRows: newValidRows,
        existingContactRows: newExistingContactRows,
      });
      setAssignedColumns(newAssignedColumns);

      setIsCombineColumns(false);
      setSelectedColumns([]);
      setConfirmCombineColumns(false);

      showNotificationBanner(
        dispatch,
        "Combined Columns",
        NotificationType.AFFIRMATIVE
      );
    },
    [
      selectedColumns,
      spreadsheet,
      assignedColumns,
      setSpreadsheet,
      setAssignedColumns,
      dispatch,
    ]
  );

  // The amount of rows that have an empty cell or censored term at the column index specified when trying to assign as full name
  const numFullNameInvalidRows = useCallback(() => {
    return spreadsheetRows.filter((row) =>
      hasEmptyOrCensoredCell(
        row,
        fullNameAlertConfirmModal.index,
        fullNameAlertConfirmModal.type
      )
    ).length;
  }, [
    spreadsheetRows,
    hasEmptyOrCensoredCell,
    fullNameAlertConfirmModal.index,
    fullNameAlertConfirmModal.type,
  ]);

  const removeFullNameInvalidRows = useCallback(() => {
    const { index: columnIndex, type } = fullNameAlertConfirmModal;
    if (columnIndex < 0) return;

    const isValidRow = (row: GuestInfo, type: "empty" | "censored" | "") =>
      !hasEmptyOrCensoredCell(row, columnIndex, type);

    let newValidRows = spreadsheet.validRows.filter((row) =>
      isValidRow(row, type)
    );
    let newExistingContactRows = spreadsheet.existingContactRows.filter((row) =>
      isValidRow(row, type)
    );

    // Proceed to assign full name column and to the spreadsheet rows only if all remaining rows are valid
    if (
      [...newValidRows, ...newExistingContactRows].every((row) =>
        isValidRow(row, "")
      )
    ) {
      newValidRows = newValidRows.map((row) => {
        return { ...row, fullName: row.dataColumns?.[columnIndex] ?? "" };
      });
      newExistingContactRows = newExistingContactRows.map((row) => {
        return { ...row, fullName: row.dataColumns?.[columnIndex] ?? "" };
      });

      const assignedColumnsCopy = deepCopy(assignedColumns);
      assignedColumnsCopy.splice(columnIndex, 1, FullNameColumnLabel);
      setAssignedColumns(assignedColumnsCopy);
    }

    setSpreadsheet({
      ...spreadsheet,
      validRows: newValidRows,
      existingContactRows: newExistingContactRows,
    });
    setFullNameAlertConfirmModal({ type: "", index: -1 });

    showNotificationBanner(dispatch, "Removed Rows", NotificationType.NEGATIVE);
  }, [
    fullNameAlertConfirmModal,
    spreadsheet,
    setSpreadsheet,
    dispatch,
    hasEmptyOrCensoredCell,
    assignedColumns,
    setAssignedColumns,
  ]);

  const columnOnPress = useCallback(
    (questionId: string, index: number) => {
      if (questionId !== "") {
        const formQuestion = savedQuestions.find(
          (question) => question.id === questionId
        );
        setFormQuestionItem(formQuestion);
      }
      // Auto generate the options for the question
      const questionOptions = spreadsheetRows
        .map((row) => (row.dataColumns ? row.dataColumns[index] : ""))
        .filter((option) => option !== "");
      setNewQuestionOptions(uniqueVals(questionOptions));
      setQuestionsVisible(true);
    },
    [savedQuestions, spreadsheetRows]
  );

  const dropdownItems: DropdownMenuItem[] = useMemo(() => {
    const dropdownItems: DropdownMenuItem[] = [
      {
        title: "Assign as Full Name",
        key: "full_name",
        onPress: () => assignFullName(columnIndex),
      },
      {
        title: "Custom Data",
        icon: "ion:arrow-forward",
        key: "custom_data",
        onPress: () => columnOnPress("", columnIndex),
      },
    ];

    return dropdownItems;
  }, [assignFullName, columnIndex, columnOnPress]);

  const hasDataColumns = useMemo(
    () =>
      spreadsheetRows.length > 0
        ? spreadsheetRows[0].dataColumns &&
          spreadsheetRows[0].dataColumns.length > 0
        : false,
    [spreadsheetRows]
  );

  const renderColumnTitle = useCallback(
    (columnId: string, index: number) => {
      const formQuestion = savedQuestions.find(
        (question) => question.id === columnId
      );
      const isAssignedColumn = formQuestion || columnId === FullNameColumnLabel;
      return (
        <div
          className="AlignedRow"
          style={{
            gap: 10,
            justifyContent: "center",
            cursor: columnId === FullNameColumnLabel ? "default" : "pointer",
          }}
        >
          <span>
            {formQuestion
              ? formQuestion.formInput.label
              : columnId === FullNameColumnLabel
                ? FullNameColumnLabel
                : isCombineColumns
                  ? "Select Column"
                  : "Assign Column"}
          </span>
          {isCombineColumns && !isAssignedColumn ? (
            <CustomCheckbox
              checked={selectedColumns.includes(index)}
              onChange={() => {
                if (selectedColumns.includes(index)) {
                  setSelectedColumns(
                    selectedColumns.filter((col) => col !== index)
                  );
                } else {
                  setSelectedColumns([...selectedColumns, index]);
                }
              }}
              sx={{ padding: 0, height: 17, width: 17 }}
              altColor={Colors.BLACK}
            />
          ) : columnId !== FullNameColumnLabel ? (
            <Icon
              icon="mdi:chevron-down-box"
              height={17}
              style={{ marginBottom: -2 }}
            />
          ) : assignedColumns.length > 1 ? (
            <Icon
              icon="mdi:close-box"
              height={17}
              style={{ marginBottom: -2, cursor: "pointer" }}
              onClick={() => unassignFullName(index)}
            />
          ) : null}
        </div>
      );
    },
    [
      assignedColumns.length,
      isCombineColumns,
      savedQuestions,
      selectedColumns,
      unassignFullName,
    ]
  );

  return (
    <>
      <div
        className="ColumnNormal Centering"
        style={{ gap: 24, paddingBlock: 30 }}
      >
        <div
          className="AlignedRow"
          style={{
            position: "relative",
            width: "100%",
            justifyContent: "center",
          }}
        >
          <div className="ColumnNormal" style={{ gap: 7, textAlign: "center" }}>
            <span style={styles.headerText}>
              Assign imported columns to existing data
            </span>
            <span style={styles.subtext}>
              Assign columns to existing data, create new column types, or
              combine columns.
              <br />
              Review before continuing.
            </span>
          </div>
          <div
            style={{
              position: "absolute",
              right: 14,
              top: 10,
            }}
          >
            {isCombineColumns ? (
              <div className="AlignedRow" style={{ gap: 10 }}>
                <RectangleButton
                  buttonLabel="Cancel"
                  onPress={() => {
                    setIsCombineColumns(false);
                    setSelectedColumns([]);
                  }}
                  altColor={Colors.WHITE1}
                  altTextColor={Colors.BLACK}
                  containerStyles={{
                    border: `0.5px solid ${Colors.GRAY2}`,
                    padding: 14,
                  }}
                />
                <RectangleButton
                  buttonLabel="Combine"
                  onPress={() => setConfirmCombineColumns(true)}
                  altColor={Colors.BLACK}
                  altTextColor={Colors.WHITE}
                  disabled={selectedColumns.length < 2}
                  containerStyles={{ padding: 14 }}
                />
              </div>
            ) : (
              <RectangleButton
                buttonLabel="Combine Columns"
                onPress={() => setIsCombineColumns(true)}
                altColor={Colors.WHITE1}
                altTextColor={Colors.BLACK}
                containerStyles={{
                  border: `0.5px solid ${Colors.GRAY2}`,
                  padding: 14,
                }}
              />
            )}
          </div>
        </div>
        <div
          className="ColumnNormal onboarding-import-assign-target"
          style={{
            overflowX: "auto",
            width: hasDataColumns ? "100%" : "auto",
          }}
        >
          <table style={{ marginInline: 40, paddingBottom: 20 }}>
            <thead>
              <tr>
                <th style={styles.leftCellContainer}></th>
                <th style={{ ...styles.cellContainer, textAlign: "center" }}>
                  Phone Number
                </th>
                {assignedColumns.map((columnId, index) => {
                  return (
                    <th
                      key={index}
                      style={{
                        ...styles.cellContainer,
                        color: columnId ? Colors.BLACK : Colors.ORANGE1,
                      }}
                      onClick={() => {
                        if (!isCombineColumns) {
                          if (!columnId && foundFullNameColumn) {
                            // Column not assigned and full name column found
                            columnOnPress("", index);
                          } else if (
                            columnId &&
                            columnId !== FullNameColumnLabel
                          ) {
                            // Column is assigned and not the full name column
                            columnOnPress(columnId, index);
                          }
                          setColumnIndex(index);
                        }
                      }}
                    >
                      {foundFullNameColumn ||
                      columnId !== "" ||
                      isCombineColumns ? (
                        renderColumnTitle(columnId, index)
                      ) : (
                        <div
                          style={{
                            width: columnId !== "" ? undefined : 140,
                            margin: "auto",
                          }}
                        >
                          <DropdownMenu
                            dropdownItems={dropdownItems}
                            trigger={["click"]}
                            placement="bottomLeft"
                            altWidth={200}
                          >
                            {renderColumnTitle(columnId, index)}
                          </DropdownMenu>
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {spreadsheetRows.slice(0, 10).map((row, index) => {
                const rowItems = [row.phoneNumber, ...(row.dataColumns ?? [])];
                return (
                  <tr key={index}>
                    <td
                      style={{
                        ...styles.leftCellContainer,
                        textAlign: "center",
                        backgroundColor: Colors.WHITE1,
                      }}
                    >
                      {index + 1}
                    </td>
                    {rowItems.map((item, index) => (
                      <td
                        style={{
                          ...styles.cellContainer,
                          textAlign: "center",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 500,
                        }}
                        key={index}
                      >
                        {item}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {spreadsheetRows.length > 7 ? (
            <div className="Centering" style={{ paddingTop: 20 }}>
              <span style={{ color: Colors.GRAY1 }}>
                {spreadsheetRows.length - 10} More Rows...
              </span>
            </div>
          ) : null}
        </div>
      </div>
      {questionsVisible ? (
        <FormSavedQuestions
          type={FormSavedQuestionsType.ASSIGN}
          isVisible={questionsVisible}
          setIsVisible={(isVisible: boolean) => {
            setQuestionsVisible(isVisible);
            setFormQuestionItem(undefined);
          }}
          assignOnPress={assignQuestionOnPress}
          selectedAssignedQuestion={formQuestionItem}
          newQuestionOptions={newQuestionOptions}
        />
      ) : null}
      {confirmCombineColumns ? (
        <CombineColumnsModal
          setIsVisible={setConfirmCombineColumns}
          saveOnPress={combineColumnsOnPress}
          spreadsheetRows={spreadsheetRows}
          selectedColumns={selectedColumns}
          setSelectedColumns={setSelectedColumns}
        />
      ) : null}
      {fullNameAlertConfirmModal.type ? (
        <PopupModalContainer
          valueComp={
            <div className="ColumnNormal" style={{ gap: 14 }}>
              <Icon
                icon="ion:warning"
                height={40}
                style={{ color: Colors.BLACK }}
              />
              <div className="ColumnNormal" style={{ gap: 7 }}>
                <span className="sectionTitle">
                  This column cannot be assigned as Full Name
                </span>
                <span className="bodySubtext">
                  {fullNameAlertConfirmModal.type === "empty"
                    ? "All contacts you upload to Markit require a Full Name. Therefore the selected column cannot contain any empty entries."
                    : `All contacts you upload to Markit cannot contain censored terms. The following censored terms were detected: ${censoredTermsFound(
                        fullNameAlertConfirmModal.index
                      ).join(", ")}.`}
                </span>
              </div>
            </div>
          }
          fixedBottomContent={
            <div className="ColumnNormal">
              <HorizontalDivider altMargin={14} />
              <div
                className="ColumnNormal"
                style={{ gap: 10, paddingInline: 14 }}
              >
                <RectangleButton
                  buttonLabel={"Select a Different Column"}
                  onPress={() =>
                    setFullNameAlertConfirmModal({ type: "", index: -1 })
                  }
                  altTextColor={Colors.BLACK}
                  altColor={Colors.WHITE1}
                  containerStyles={{
                    padding: "14px 12px",
                    border: `0.5px solid ${Colors.GRAY2}`,
                  }}
                />
                {spreadsheetRows.length > 1 ? (
                  <RectangleButton
                    buttonLabel={`Remove ${numFullNameInvalidRows()} ${
                      fullNameAlertConfirmModal.type === "empty"
                        ? "Empty"
                        : "Invalid"
                    } ${handlePluralString("Row", numFullNameInvalidRows())}`}
                    onPress={removeFullNameInvalidRows}
                    altTextColor={Colors.WHITE}
                    altColor={Colors.BLACK}
                    containerStyles={{ padding: "14px 12px" }}
                  />
                ) : null}
              </div>
            </div>
          }
          noExit
          noDivider
          closeModal={() =>
            setFullNameAlertConfirmModal({ type: "", index: -1 })
          }
          modalContentWidth={PopupModalContainerWidth.SMALL}
        />
      ) : null}
    </>
  );
};

export default AssignImportedContacts;
